import React, { useCallback, useEffect, useState } from 'react';

import { Grid } from '@lp/ds-next';
import { Tabs } from 'antd';
import queryString from 'query-string';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { IShipment } from '../../../features/shipments/models/shipment';
import BoxChecklist from '../components/BoxChecklist';
import BoxConfig from '../components/BoxConfig';
import BoxesSticker from '../components/BoxesSticker';
import BoxFirmwareHistory from '../components/BoxFirmwareHistory';
import BoxHardware from '../components/BoxHardware';
import BoxLastLocationMap from '../components/BoxLastLocationMap';
import { BoxLifecycle } from '../components/BoxLifecycle';
import BoxManagement from '../components/BoxManagement';
import BoxNotes from '../components/BoxNotes/BoxNotes';
import BoxQualityCheck from '../components/BoxQualityCheck';
import BoxTelemetryEvents from '../components/BoxTelemetryEvents';
import GoToBoxShipmentButton from '../components/GoToBoxShipmentButton';
import ProductDetailsForm from '../components/ProductDetailsForm';
import RemoteControl from '../components/RemoteControl/RemoteControl';
import SimVersion from '../components/SimVersions';
import useGetBox from '../hooks/useGetBox';
import PageTitle from '@/components/layout/PageTitle';
import { scopes } from '@/config';
import { useApiClient } from '@/helpers/ApiClients';
import { IPaginatedStore } from '@/helpers/paginatedStoreHelpers';
import useAppState from '@/hooks/useAppState';

const ActionButtonBar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ProductDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const { box, getBox } = useGetBox(id);
  const [tab, setTab] = useState('telemetry');
  const [tabItems, setTabItems] = useState<any[]>([]);
  const shipmentService = useApiClient('shipment');
  const [currentShipment, setCurrentShipment] = useState<IShipment>();
  const [nbShipment, setNbShipment] = useState<number>(0);
  const userScopes = useAppState((state) => state.scopes);

  const findCurrentShipment = useCallback(
    (data) => {
      // Early return: No shipments
      if (!data.items || data.items.length === 0) {
        setCurrentShipment(undefined);
        setNbShipment(0);

        return;
      }

      // Filter shipments that are physically on the box
      const activeShipments = data.items.filter(
        (s) =>
          !s.isArchived &&
          (s.contractState === 'TRANSFERRED' || s.contractState === 'DEPLOYED')
      );

      // Update count
      setNbShipment(activeShipments.length);

      // Early return: No active shipments
      if (activeShipments.length === 0) {
        setCurrentShipment(undefined);

        return;
      }

      // Select most recent by updatedAt
      const mostRecentShipment = activeShipments.reduce((prev, current) =>
        (current.updatedAt || 0) > (prev.updatedAt || 0) ? current : prev
      );

      setCurrentShipment(mostRecentShipment);
    },
    [setCurrentShipment]
  );

  const refreshBoxShipment = useCallback(
    (boxId: string): void => {
      // Query params
      const params = queryString.stringify({
        boxId,
        lastdeployed: true,
        sortBy: 'deployedAt',
        order: 'asc',
      });

      shipmentService
        .get<IPaginatedStore<any, {}>>(`/shipments?${params}`)
        .then(({ data }) => {
          // findCurrentShipment va filtrer et mettre à jour nbShipment
          findCurrentShipment(data);
        })
        .catch((error) => {
          console.error(
            '[refreshBoxShipment] Error fetching shipments:',
            error
          );
          setCurrentShipment(undefined);
          setNbShipment(0);
        });
    },
    [shipmentService, findCurrentShipment]
  );

  // Callback to refresh after cleanup
  const handleCleanupComplete = useCallback(() => {
    // refresh box information
    getBox();
    // refresh shipments
    if (box.id) {
      refreshBoxShipment(box.id);
    }
  }, [getBox, box.id, refreshBoxShipment]);

  useEffect(() => {
    if (box.id) {
      refreshBoxShipment(box.id);
      const items = [
        {
          label: 'Telemetry Events',
          key: 'telemetry',
          children: <BoxTelemetryEvents id={box.id} />,
          forceRender: true,
        },
        {
          label: 'Firmware',
          key: 'firmware',
          children: <BoxFirmwareHistory box={box} />,
        },
        {
          label: 'Hardware',
          key: 'hardware',
          children: <BoxHardware box={box} />,
        },
        {
          label: 'Connectivity',
          key: 'connectivity',
          children: <SimVersion box={box} />,
        },
        {
          label: 'Product Sticker',
          key: 'production-sticker',
          children: <BoxesSticker box={box} />,
        },
        {
          label: 'Box Status',
          key: 'box-status',
          children: <BoxChecklist boxId={box.id!} />,
        },
        {
          label: 'Notes',
          key: 'notes',
          children: <BoxNotes boxId={box.id!} />,
        },
        {
          label: 'Firmware Config',
          key: 'firmware-config',
          children: <BoxConfig boxId={box.id!} />,
        },
        {
          label: 'Box Quality Check',
          key: 'box-quality-check',
          children: <BoxQualityCheck boxId={box.id!} />,
        },
        {
          label: 'Lifecycle',
          key: 'lifecycle',
          children: <BoxLifecycle boxId={box.lpUI} />,
        },
        {
          label: 'Map',
          key: 'map',
          children: <BoxLastLocationMap boxId={box.id} thingName={box.lpUI} />,
        },
      ];

      const responsibilityHistoryTab = {
        label: 'Responsibility History',
        key: 'box-management',
        children: <BoxManagement id={box.id} />,
      };

      items.splice(4, 0, responsibilityHistoryTab);

      setTabItems(items);
    }
  }, [
    box,
    findCurrentShipment,
    shipmentService,
    userScopes,
    refreshBoxShipment,
  ]);

  return (
    <Grid container justifyContent="center">
      <Grid item mobile={11}>
        <PageTitle title="PRODUCT DETAILS" />
      </Grid>
      <Grid
        item
        mobile={11}
        container
        justifyContent="flex-end"
        style={{ gap: '0.5rem' }}
      >
        {userScopes.includes(scopes.MANAGE_PRODUCT) && id && (
          <RemoteControl
            shipment={currentShipment}
            onCleanupComplete={handleCleanupComplete}
          />
        )}
        <ActionButtonBar>
          <GoToBoxShipmentButton boxId={box.id} shipment={currentShipment} />
        </ActionButtonBar>
      </Grid>
      <Grid item mobile={11}>
        <ProductDetailsForm
          box={box}
          shipment={currentShipment}
          nbShipment={nbShipment}
        />
      </Grid>
      <Grid item mobile={11} style={{ marginTop: '2rem' }}>
        <Tabs
          type="card"
          activeKey={tab}
          onTabClick={(key) => setTab(key)}
          items={tabItems}
          data-testid="product-details-tabs"
        />
      </Grid>
    </Grid>
  );
};

export default ProductDetailView;
